import { FC, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import dayjs, { Dayjs } from 'dayjs';

import './TeamDayCurrentTimeMarkers.scss';
import { HOUR_BLOCK_HEIGHT } from '../../CalendarView/CalendarTimeline/constants';

export interface TeamDayCurrentTimeMarkerResource {
  id: string;
  name: string;
  timezone: string;
  isCalendarShared: boolean;
}

interface TeamDayCurrentTimeMarkersProps {
  resources: TeamDayCurrentTimeMarkerResource[];
  now: Dayjs;
  selectedDate: Dayjs;
  containerRef: React.RefObject<HTMLDivElement | null>;
}

const timezoneValidityCache = new Map<string, boolean>();

const resolveTimezone = (timezone: string) => {
  if (!timezone) {
    return dayjs.tz.guess();
  }

  const cached = timezoneValidityCache.get(timezone);
  if (cached === false) {
    return dayjs.tz.guess();
  }
  if (cached === true) {
    return timezone;
  }

  let valid = false;
  try {
    valid = dayjs().tz(timezone).isValid();
  } catch {
    valid = false;
  }
  timezoneValidityCache.set(timezone, valid);
  return valid ? timezone : dayjs.tz.guess();
};

const TeamDayCurrentTimeMarkers: FC<TeamDayCurrentTimeMarkersProps> = ({
  resources,
  now,
  selectedDate,
  containerRef,
}) => {
  const [daySlots, setDaySlots] = useState<HTMLElement[]>([]);

  useEffect(() => {
    const root = containerRef.current;

    if (!root) {
      return undefined;
    }

    const refresh = () => {
      const slots = Array.from(
        root.querySelectorAll<HTMLElement>('.rbc-time-content > .rbc-day-slot')
      );
      setDaySlots(slots);
    };

    refresh();

    const observer = new MutationObserver(refresh);
    observer.observe(root, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [containerRef, resources.length]);

  const isToday = useMemo(() => selectedDate.isSame(now, 'day'), [selectedDate, now]);

  if (!daySlots.length || !isToday) {
    return null;
  }

  return (
    <>
      {resources.map((resource, resourceIndex) => {
        const slot = daySlots[resourceIndex];

        if (!slot || !resource.isCalendarShared) {
          return null;
        }

        const timezone = resolveTimezone(resource.timezone);
        const localNow = now.tz(timezone);
        const top = (localNow.hour() + localNow.minute() / 60) * HOUR_BLOCK_HEIGHT;

        return createPortal(
          <div
            className="team-day-current-time-markers__line"
            style={{ top: `${top}px` }}
            aria-label={`${resource.name} current time ${localNow.format('h:mm A')}`}
          >
            <span className="team-day-current-time-markers__dot" aria-hidden="true" />
          </div>,
          slot,
          `team-day-current-time-marker-${resource.id}`
        );
      })}
    </>
  );
};

export default TeamDayCurrentTimeMarkers;
